/**
 * SettingsEventDefaultsSection
 *
 * Event default settings: calendar, duration, reminder, status, color,
 * dim past events, and show weekends.
 */

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings } from 'lucide-react';
import type { CalendarCollection, CalendarSettings } from '../types';
import { Section, ToggleRow, Field } from './settings-helpers';

interface SettingsEventDefaultsSectionProps {
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
  /** The user's calendars, for the default-calendar choice; absent or empty hides it. */
  calendars?: CalendarCollection[];
}

/** Sentinel for "no preference" — a new event then lands on the enabled
 *  default calendar (`resolveNewEventCalendarId`). Mirrors the bookings
 *  default in `SettingsSheet`. */
const NO_DEFAULT_CALENDAR = '__default__';

export function SettingsEventDefaultsSection({
  settings,
  onUpdateSettings,
  calendars,
}: SettingsEventDefaultsSectionProps) {
  return (
    <Section icon={Settings} title="Event Defaults">
      {calendars && calendars.length > 0 && (
        <Field label="Default calendar for new events">
          <Select
            value={settings.defaultCalendarId ?? NO_DEFAULT_CALENDAR}
            onValueChange={(v) =>
              onUpdateSettings({ defaultCalendarId: v === NO_DEFAULT_CALENDAR ? null : v })
            }
          >
            <SelectTrigger aria-label="Default calendar for new events">
              <SelectValue placeholder="Default calendar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DEFAULT_CALENDAR}>Default calendar</SelectItem>
              {calendars.map((cal) => (
                <SelectItem key={cal.id} value={cal.id}>
                  {cal.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <Field label="Default event duration">
        <Select
          value={String(settings.defaultEventDuration ?? 60)}
          onValueChange={(v) => onUpdateSettings({ defaultEventDuration: parseInt(v, 10) })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="15">15 minutes</SelectItem>
            <SelectItem value="30">30 minutes</SelectItem>
            <SelectItem value="45">45 minutes</SelectItem>
            <SelectItem value="60">1 hour</SelectItem>
            <SelectItem value="90">1.5 hours</SelectItem>
            <SelectItem value="120">2 hours</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Default event reminder">
        <Select
          value={
            settings.defaultEventReminder == null
              ? '__none__'
              : String(settings.defaultEventReminder)
          }
          onValueChange={(v) =>
            onUpdateSettings({
              defaultEventReminder: v === '__none__' ? null : parseInt(v, 10),
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            <SelectItem value="0">At time of event</SelectItem>
            <SelectItem value="5">5 minutes before</SelectItem>
            <SelectItem value="15">15 minutes before</SelectItem>
            <SelectItem value="30">30 minutes before</SelectItem>
            <SelectItem value="60">1 hour before</SelectItem>
            <SelectItem value="120">2 hours before</SelectItem>
            <SelectItem value="1440">1 day before</SelectItem>
            <SelectItem value="10080">1 week before</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Default event status">
        <Select
          value={settings.defaultEventStatus ?? 'confirmed'}
          onValueChange={(v) =>
            onUpdateSettings({ defaultEventStatus: v as 'confirmed' | 'tentative' })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="tentative">Tentative</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Default event color">
        <div className="flex items-center gap-2">
          <Select
            value={settings.defaultEventColor == null ? '__calendar__' : '__custom__'}
            onValueChange={(v) =>
              onUpdateSettings({ defaultEventColor: v === '__calendar__' ? null : '#3b82f6' })
            }
          >
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__calendar__">Use calendar color</SelectItem>
              <SelectItem value="__custom__">Custom color</SelectItem>
            </SelectContent>
          </Select>
          {settings.defaultEventColor != null && (
            <Input
              type="color"
              value={settings.defaultEventColor}
              onChange={(e) => onUpdateSettings({ defaultEventColor: e.target.value })}
              className="w-16 h-8 p-0.5 shrink-0"
            />
          )}
        </div>
      </Field>

      <ToggleRow
        label="Dim past events"
        description="Reduce opacity of events that have already ended."
        checked={settings.dimPastEvents ?? true}
        onCheckedChange={(checked) => onUpdateSettings({ dimPastEvents: checked })}
      />

      <ToggleRow
        label="Show weekends"
        checked={settings.showWeekends ?? true}
        onCheckedChange={(checked) => onUpdateSettings({ showWeekends: checked })}
      />
    </Section>
  );
}
